/**
 * KNK-1.53 / FR-43 — Settings → Knock Mode → Import from another app.
 *
 * Choose the source and the file, check the columns and map their statuses, preview (the server's
 * dry run), then import. A file over the server's row limit goes in parts, each its own import and
 * its own undo. Each import is listed with Undo while the server says it can still be undone.
 * Rules and copy: `lib/knockImport.ts`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RotateCcw, Upload } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import {
  FIELD_LABELS,
  IMPORT_COPY as COPY,
  IMPORT_FIELDS,
  autoMapColumns,
  batchLine,
  buildImportRows,
  canApply,
  codesPayload,
  distinctCodes,
  mappingWarnings,
  parseCsvText,
  previewLines,
  skipReasonLabel,
  splitIntoParts,
  suggestCodeMapping,
  type CodeMap,
  type ColumnMap,
  type ImportBatch,
  type ImportField,
  type ImportOverview,
  type ImportPreview,
} from '@/lib/knockImport';
import {
  applyImport,
  fetchImportOverview,
  previewImport,
  revertImport,
  saveImportPreset,
  type ImportRequest,
} from '@/services/knockImportService';

const NONE = '__none';
const SKIP = '__skip';
const UNSET = '__unset';

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

interface LoadedFile {
  name: string;
  headers: string[];
  records: Record<string, string>[];
}

export function KnockImportCard() {
  const { toast } = useToast();
  const [overview, setOverview] = useState<ImportOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sourceKey, setSourceKey] = useState('salesrabbit');
  const [file, setFile] = useState<LoadedFile | null>(null);
  const [columns, setColumns] = useState<ColumnMap>({});
  const [codes, setCodes] = useState<CodeMap>({});
  const [part, setPart] = useState(0);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [busy, setBusy] = useState<string | null>('load');
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try {
      setOverview(await fetchImportOverview());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const preset = overview?.presets.find((p) => p.sourceKey === sourceKey) ?? overview?.presets[0] ?? null;
  const rows = useMemo(() => (file ? buildImportRows(file.records, columns) : []), [file, columns]);
  const codeList = useMemo(() => distinctCodes(rows), [rows]);
  const parts = useMemo(() => splitIntoParts(rows, overview?.maxRows ?? 500), [rows, overview?.maxRows]);
  const warnings = file ? mappingWarnings(columns) : [];
  const blocked = warnings[0] === COPY.needAddress;

  // A new file or source re-reads the mapping from the preset.
  const remap = (f: LoadedFile, key: string) => {
    const p = overview?.presets.find((x) => x.sourceKey === key);
    if (!p || !overview) return;
    const map = autoMapColumns(f.headers, p);
    setColumns(map);
    setCodes(suggestCodeMapping(distinctCodes(buildImportRows(f.records, map)), p, overview.dispositions));
    setPart(0);
    setPreview(null);
  };

  const onFile = async (picked: File | undefined) => {
    if (!picked) return;
    try {
      const { headers, records } = parseCsvText(await picked.text());
      const loaded = { name: picked.name, headers, records };
      setFile(loaded);
      setError(null);
      remap(loaded, sourceKey);
    } catch (e) {
      setFile(null);
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const setColumn = (field: ImportField, header: string) => {
    const next = { ...columns };
    if (header === NONE) delete next[field];
    else next[field] = header;
    setColumns(next);
    setPreview(null);
    // A different status column brings different codes: suggest for any new ones.
    if (field === 'disposition' && file && preset && overview) {
      const fresh = suggestCodeMapping(distinctCodes(buildImportRows(file.records, next)), preset, overview.dispositions);
      setCodes({ ...fresh, ...codes });
    }
  };

  const request = (): ImportRequest | null =>
    file && preset ? { sourceKey: preset.sourceKey, fileName: file.name, rows: parts[part] ?? [], codes: codesPayload(codes) } : null;

  const fail = (e: unknown) =>
    toast({ title: 'Not imported', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });

  const runPreview = async () => {
    const r = request();
    if (!r) return;
    setBusy('preview');
    try {
      setPreview(await previewImport(r));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const apply = async () => {
    const r = request();
    if (!r || !preview) return;
    setBusy('apply');
    try {
      const batches = await applyImport(r, preview);
      setOverview((o) => (o ? { ...o, batches } : o));
      toast({ title: parts.length > 1 ? `${COPY.applied}: part ${part + 1} of ${parts.length}` : COPY.applied });
      setPreview(null);
      if (part + 1 < parts.length) setPart(part + 1);
      else setFile(null);
    } catch (e) {
      fail(e);
      // After a stale refusal, show the numbers as they are now.
      await runPreview();
    } finally {
      setBusy(null);
    }
  };

  const undo = async (b: ImportBatch) => {
    setBusy(b.id);
    try {
      const batches = await revertImport(b.id);
      setOverview((o) => (o ? { ...o, batches } : o));
      toast({ title: COPY.undone });
    } catch (e) {
      fail(e);
      await load();
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    if (!preset) return;
    setBusy('save');
    try {
      await saveImportPreset(preset, columns, codes);
      toast({ title: COPY.presetSaved });
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const dispositions = overview?.dispositions ?? [];

  return (
    <Card data-testid="knock-import">
      <CardHeader>
        <CardTitle>{COPY.title}</CardTitle>
        <CardDescription>{COPY.hint}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {busy === 'load' && !overview && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        )}

        {overview && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="knock-import-source">{COPY.source}</Label>
                <Select
                  value={preset?.sourceKey}
                  onValueChange={(key) => {
                    setSourceKey(key);
                    if (file) remap(file, key);
                  }}
                >
                  <SelectTrigger id="knock-import-source" data-testid="knock-import-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {overview.presets.map((p) => (
                      <SelectItem key={p.sourceKey} value={p.sourceKey}>
                        {p.name}
                        {!p.builtin && ' (saved)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="knock-import-file">{COPY.file}</Label>
                <input
                  id="knock-import-file"
                  data-testid="knock-import-file"
                  type="file"
                  accept=".csv,text/csv"
                  className="block w-full text-sm text-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-background file:px-3 file:py-1.5 file:text-sm file:text-foreground"
                  onChange={(e) => void onFile(e.target.files?.[0])}
                />
                {file && (
                  <p className="text-xs text-muted-foreground">
                    {file.name}: {rows.length} row{rows.length === 1 ? '' : 's'}
                    {parts.length > 1 && `, imported in ${parts.length} parts of up to ${overview.maxRows}`}
                  </p>
                )}
              </div>
            </div>

            {file && (
              <>
                <section className="space-y-2" data-testid="knock-import-columns">
                  <div>
                    <p className="text-sm font-medium text-foreground">{COPY.columnsTitle}</p>
                    <p className="text-xs text-muted-foreground">{COPY.columnsHint}</p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {IMPORT_FIELDS.map((f) => (
                      <div key={f} className="min-w-0 space-y-1">
                        <Label className="text-xs text-muted-foreground">{FIELD_LABELS[f]}</Label>
                        <Select value={columns[f] ?? NONE} onValueChange={(v) => setColumn(f, v)}>
                          <SelectTrigger className="h-9" data-testid={`knock-import-col-${f}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>{COPY.notInFile}</SelectItem>
                            {file.headers.filter((h) => h !== '').map((h) => (
                              <SelectItem key={h} value={h}>{h}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  {warnings.length > 0 && (
                    <ul className="space-y-1 text-xs text-muted-foreground" data-testid="knock-import-warnings">
                      {warnings.map((w) => <li key={w}>{w}</li>)}
                    </ul>
                  )}
                </section>

                {codeList.length > 0 && (
                  <section className="space-y-2" data-testid="knock-import-codes">
                    <div>
                      <p className="text-sm font-medium text-foreground">{COPY.codesTitle}</p>
                      <p className="text-xs text-muted-foreground">{COPY.codesHint}</p>
                    </div>
                    <ul className="divide-y divide-border rounded-md border border-border">
                      {codeList.map(({ code, count }) => {
                        const v = codes[code];
                        return (
                          <li key={code} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
                            <p className="min-w-0 break-words text-sm text-foreground">
                              {code} <span className="text-xs text-muted-foreground">({count})</span>
                            </p>
                            <Select
                              value={v === undefined ? UNSET : v === null ? SKIP : v}
                              onValueChange={(next) => {
                                setCodes({ ...codes, [code]: next === SKIP ? null : next === UNSET ? undefined : next });
                                setPreview(null);
                              }}
                            >
                              <SelectTrigger className="h-9 sm:w-72" data-testid={`knock-import-code-${code}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={UNSET}>{COPY.chooseCode}</SelectItem>
                                {dispositions.map((d) => (
                                  <SelectItem key={d.code} value={d.code}>
                                    {d.name}
                                    {!d.active && ' (archived)'}
                                  </SelectItem>
                                ))}
                                <SelectItem value={SKIP}>{COPY.skipCode}</SelectItem>
                              </SelectContent>
                            </Select>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => void runPreview()} disabled={busy !== null || blocked} data-testid="knock-import-preview-btn">
                    {busy === 'preview' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {COPY.preview}
                    {parts.length > 1 && ` part ${part + 1} of ${parts.length}`}
                  </Button>
                  <Button variant="outline" onClick={() => void save()} disabled={busy !== null || !preset}>
                    {busy === 'save' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {COPY.savePreset}
                  </Button>
                </div>

                {preview && (
                  <div className="space-y-3 rounded-md border border-border p-3" data-testid="knock-import-preview">
                    <ul className="space-y-1.5 text-sm text-foreground">
                      {previewLines(preview).map((line) => (
                        <li key={line} className="flex gap-2">
                          <span aria-hidden className="text-muted-foreground">•</span>
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                    {preview.skippedRows.length > 0 && (
                      <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer">Skipped rows</summary>
                        <ul className="mt-1 space-y-0.5">
                          {preview.skippedRows.map((s) => (
                            <li key={s.row}>Row {s.row}: {skipReasonLabel(s.reason)}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                    <Button
                      onClick={() => setConfirming(true)}
                      disabled={busy !== null || !canApply(preview)}
                      data-testid="knock-import-apply-btn"
                    >
                      {busy === 'apply' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                      {COPY.apply}
                      {parts.length > 1 && ` part ${part + 1} of ${parts.length}`}
                    </Button>
                  </div>
                )}
              </>
            )}

            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">{COPY.batchesTitle}</p>
              {overview.batches.length === 0 ? (
                <p className="text-sm text-muted-foreground">{COPY.noBatches}</p>
              ) : (
                <ul className="divide-y divide-border rounded-md border border-border" data-testid="knock-import-batches">
                  {overview.batches.map((b) => (
                    <li key={b.id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`knock-import-batch-${b.id}`}>
                      <div className="min-w-0 space-y-0.5">
                        <p className="flex flex-wrap items-center gap-2 text-sm text-foreground">
                          {when(b.appliedAt)}
                          {b.revertedAt && <Badge variant="secondary">Undone</Badge>}
                        </p>
                        <p className="break-words text-xs text-muted-foreground">{batchLine(b)}</p>
                        {!b.undoable && !b.revertedAt && b.revertBlocker && (
                          <p className="text-xs text-muted-foreground">{b.revertBlocker}</p>
                        )}
                      </div>
                      {b.undoable && (
                        <Button size="sm" variant="outline" className="shrink-0 self-start sm:self-center" disabled={busy !== null} onClick={() => void undo(b)}>
                          {busy === b.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                          {COPY.undo}
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-muted-foreground">{COPY.undoHint}</p>
            </div>
          </>
        )}
      </CardContent>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{COPY.apply} {file?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {preview && previewLines(preview).join(' ')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirming(false);
                void apply();
              }}
            >
              {COPY.apply}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
